import { Action, createReducer, on } from '@ngrx/store';

import { Post } from '../models/core.models';

import {
  loadPost,
  loadPostFail,
  translatePost,
  updateSynopsis,
  openPostDetails,
  generateAISynopsis,
  updatePostTranslation,
  createPostQuickStoryReport,
  updatePostQuickStoryReport,
  translatePostFail,
  generateAISynopsisFail,
  updatePostTranslationFail,
  createPostQuickStoryReportFail,
  loadPostSuccess,
  translatePostSuccess,
  createSynopsisSuccess,
  deleteSynopsisSuccess,
  updateSynopsisSuccess,
  generateAISynopsisSuccess,
  updatePostTranslationSuccess,
  transitionToWorkflowStageSuccess,
  updatePostQuickStoryReportSuccess,
  createPostQuickStoryReportSuccess,
  applyPostAlgorithmSuccess,
} from '../actions/post.actions';
import { feedUnloaded, loadFeedPostsSuccess } from '../actions/feed.actions';

import { produce } from 'immer';

export interface PostState {
  selectedPostId: string | null;
  posts: { [id: string]: Post | null };
  loadingPosts: { [id: string]: boolean };
  loadingSynopsis: { [id: string]: boolean };
  loadingTranslations: { [id: string]: boolean };
  loadingQuickStoryReport: { [id: string]: boolean };
}

export const initialState: PostState = {
  selectedPostId: null,
  posts: {},
  loadingPosts: {},
  loadingSynopsis: {},
  loadingTranslations: {},
  loadingQuickStoryReport: {},
};

const _postReducer = createReducer(
  initialState,
  // Load feed posts success action is used to initialize the state with the first post
  on(loadFeedPostsSuccess, (state, { posts, totalItemsCount }) => {
    const firstPost = posts?.length > 0 ? posts[0] : null;
    return {
      ...state,
      selectedPostId: posts?.length > 0 ? posts[0].id : null,
      posts: {
        [firstPost?.id]: firstPost,
      },
    };
  }),

  on(loadPost, (state, { postId }) => ({
    ...state,
    loadingPosts: { ...state.loadingPosts, [postId]: true },
    posts: { ...state.posts, [postId]: null },
  })),

  on(loadPostFail, (state, { postId }) => ({
    ...state,
    loadingPosts: { ...state.loadingPosts, [postId]: false },
  })),

  on(loadPostSuccess, (state, { post }) => ({
    ...state,
    posts: { ...state.posts, [post.id]: post },
    loadingPosts: { ...state.loadingPosts, [post.id]: false },
  })),

  on(createSynopsisSuccess, (state, { post }) => ({
    ...state,
    posts: { ...state.posts, [post.id]: post },
  })),

  on(updateSynopsis, (state, { postId }) => ({
    ...state,
    loadingSynopsis: { ...state.loadingSynopsis, [postId]: true },
  })),

  on(
    updateSynopsisSuccess,
    produce((draft, { postId, postSynopsis }) => {
      const post = draft.posts[postId];
      if (!post) return;

      const synopsis = post.synopsis;

      for (const key of Object.keys(synopsis)) {
        if (synopsis[key].id === postSynopsis.id) {
          synopsis[key] = postSynopsis;
        }
      }

      draft.loadingSynopsis[postId] = false;
    }),
  ),

  on(generateAISynopsis, (state, { postId }) => ({
    ...state,
    loadingSynopsis: { ...state.loadingSynopsis, [postId]: true },
  })),

  on(generateAISynopsisSuccess, (state, { post }) => ({
    ...state,
    posts: { ...state.posts, [post.id]: post },
    loadingSynopsis: { ...state.loadingSynopsis, [post.id]: false },
  })),

  on(generateAISynopsisFail, (state, { postId }) => ({
    ...state,
    loadingSynopsis: { ...state.loadingSynopsis, [postId]: false },
  })),

  on(
    deleteSynopsisSuccess,
    produce((draft, { postId, id }) => {
      const post = draft.posts[postId];
      if (!post) return;

      const synopsis = post.synopsis;

      for (const key of Object.keys(synopsis)) {
        if (synopsis[key].id === id) {
          delete synopsis[key];
        }
      }
    }),
  ),

  on(updatePostTranslation, (state, { postId }) => ({
    ...state,
    loadingTranslations: { ...state.loadingTranslations, [postId]: true },
  })),

  on(updatePostTranslationSuccess, (state, { post }) => ({
    ...state,
    posts: { ...state.posts, [post.id]: post },
    loadingTranslations: { ...state.loadingTranslations, [post.id]: false },
  })),

  on(updatePostTranslationFail, (state, { postId }) => ({
    ...state,
    loadingTranslations: { ...state.loadingTranslations, [postId]: false },
  })),

  on(createPostQuickStoryReport, (state, { postId }) => ({
    ...state,
    loadingQuickStoryReport: {
      ...state.loadingQuickStoryReport,
      [postId]: true,
    },
  })),

  on(
    createPostQuickStoryReportSuccess,
    produce((draft, { post }) => {
      draft.posts[post.id] = post;
      draft.loadingQuickStoryReport[post.id] = false;
    }),
  ),

  on(
    createPostQuickStoryReportFail,
    produce((draft, { postId }) => {
      draft.loadingQuickStoryReport[postId] = false;
    }),
  ),

  on(
    updatePostQuickStoryReport,
    produce((draft, { postId }) => {
      draft.loadingQuickStoryReport[postId] = true;
    }),
  ),

  on(
    updatePostQuickStoryReportSuccess,
    produce((draft, { post }) => {
      draft.posts[post.id] = post;
      draft.loadingQuickStoryReport[post.id] = false;
    }),
  ),

  on(
    updatePostTranslationFail,
    produce((draft, { postId }) => {
      draft.loadingQuickStoryReport[postId] = false;
    }),
  ),

  on(translatePost, (state) => ({
    ...state,
  })),

  on(
    translatePostSuccess,
    produce((draft, { postId, languageId, title, content }) => {
      const post = draft.posts[postId];
      if (!post) return;

      if (!post.translation) {
        post.translation = {};
      }

      post.translation[languageId] = {
        title,
        content,
      };
    }),
  ),

  // ! TODO: do we need this?
  on(translatePostFail, (state, { error }) => {
    return {
      ...state,
    };
  }),

  on(openPostDetails, (state, { postId }) => ({
    ...state,
    selectedPostId: postId,
  })),

  on(
    transitionToWorkflowStageSuccess,
    produce((draft, { post }) => {
      const target = draft.posts[post.id];
      if (!target) return;

      target.workflows = post.workflows;
    }),
  ),

  on(applyPostAlgorithmSuccess, (state, { posts }) => {
    const updatedPosts = { ...state.posts };

    for (const post of posts) {
      updatedPosts[post.id] = post;
    }

    return {
      ...state,
      posts: updatedPosts,
    };
  }),

  on(feedUnloaded, (state) => {
    return initialState;
  }),
);

export function postReducer(state: PostState | undefined, action: Action) {
  return _postReducer(state, action);
}
